import { useState, useMemo, useEffect, useRef } from 'react';
import { usePlans } from '@/hooks/usePlans';
import { useHighlights } from '@/hooks/useHighlights';
import { useFilters, useSetFilteredPlansCount, useFeaturedPlanIds } from '@/contexts';
import {ComparatorCard, RadioGroup, RadioOption, BottomSheet, Modal} from '@/components/molecules';
import { PLAN_TYPES, type PlanType } from '@/config/api';
import { applyFilters, mobilePredicates, internetPredicates } from '@/utils/applyFilters';
import './Comparator.scss';
import lemonSadImg from '@/assets/img/lemon-sad.svg';
import arrowRight from '@/assets/icons/arrow-right.svg';
import iconInformation from '@/assets/icons/icon-information.svg';

const ITEMS_PER_PAGE = 20;

export interface ComparatorProps {
  title?: string;
  planType?: PlanType;
  highlightFavoriteIds?: string[];
  featuredPlanIds?: string[];
}

export function Comparator({ title, planType = PLAN_TYPES.MOBILE, highlightFavoriteIds, featuredPlanIds }: ComparatorProps) {
  const { plans, loading, error } = usePlans(planType);
  const { filters, resetFilters } = useFilters();
  const featuredFromContext = useFeaturedPlanIds();
  const effectiveFeaturedIds = featuredPlanIds ?? featuredFromContext;

  const [sortBy, setSortBy] = useState<string>('popular');
  const [priceDuration, setPriceDuration] = useState<string>('1-month');
  const [visibleCount, setVisibleCount] = useState(Infinity); // SSR: everything visible in the DOM
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [animatedCount, setAnimatedCount] = useState(0);

  // Enable pagination after hydration
  useEffect(() => {
    // Deliberate post-hydration switch: SSR must ship every card in the DOM for SEO, the list is only
    // trimmed once the client takes over.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setVisibleCount(ITEMS_PER_PAGE);
  }, []);

  // BottomSheet state
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [tempSortBy, setTempSortBy] = useState<string>(sortBy);
  const [tempPriceDuration, setTempPriceDuration] = useState<string>(priceDuration);

  // Info modal state
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  // Info tooltip state (desktop)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const desktopTooltipRef = useRef<HTMLDivElement>(null);
  const tabletTooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isTooltipOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isInsideDesktop = desktopTooltipRef.current?.contains(target);
      const isInsideTablet = tabletTooltipRef.current?.contains(target);
      if (!isInsideDesktop && !isInsideTablet) {
        setIsTooltipOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isTooltipOpen]);

  const infoText = "<p>Les offres présentées sont sélectionnées par Lemon et sont triées par défaut en fonction d'un score qui privilégie notamment les offres les plus sélectionnées par les visiteurs. Les offres siglées avec \"Annonces\" sont affichées en priorité dans la liste.</p> <a href='https://lemon.fr/fonctionnement-des-comparateurs/'>Plus d'infos sur le fonctionnement des comparateurs.</a>";

  const filteredPlans = useMemo(() => {
    const predicates = planType === PLAN_TYPES.INTERNET ? internetPredicates : mobilePredicates;
    return applyFilters(plans, filters, predicates);
  }, [plans, filters, planType]);

  // Sync filtered plans count to context for FilterGroup badge
  const setFilteredPlansCount = useSetFilteredPlansCount();
  useEffect(() => {
    setFilteredPlansCount(filteredPlans.length);
  }, [filteredPlans.length, setFilteredPlansCount]);

  const sortedPlans = useMemo(() => {
    const sorted = [...filteredPlans];
    switch (sortBy) {
      case 'popular': {
        sorted.sort((a, b) => b.score - a.score);
        if (effectiveFeaturedIds.length === 0) return sorted;
        const featuredSet = new Set(effectiveFeaturedIds);
        const featuredPlans = effectiveFeaturedIds
          .map((id) => sorted.find((p) => p.id === id))
          .filter((p): p is typeof sorted[number] => Boolean(p));
        const rest = sorted.filter((p) => !featuredSet.has(p.id));
        return [...featuredPlans, ...rest];
      }
      case 'cheapest':
        return sorted.sort((a, b) => a.offer.price.price - b.offer.price.price);
      case 'datas':
        // For internet, sort by speed; for mobile, by data
        if (planType === PLAN_TYPES.INTERNET) {
          return sorted.sort((a, b) => {
            const speedA = a.extra?.internet_speed || a.extra?.download_speed || 0;
            const speedB = b.extra?.internet_speed || b.extra?.download_speed || 0;
            return speedB - speedA;
          });
        }
        return sorted.sort((a, b) => b.extra.data.data - a.extra.data.data);
      case 'tv-channels':
        return sorted.sort((a, b) => {
          const channelsA = a.extra?.included_channels_nbr ?? 0;
          const channelsB = b.extra?.included_channels_nbr ?? 0;
          return channelsB - channelsA;
        });
      default:
        return sorted;
    }
  }, [filteredPlans, sortBy, planType, effectiveFeaturedIds]);

  // Highlights are computed on the sorted, filtered plans
  const highlightIds = highlightFavoriteIds ?? [];
  const plansWithHighlights = useHighlights(sortedPlans, {
    highlightIds,
    planType,
  });

  const hasMoreItems = visibleCount < plansWithHighlights.length;

  const handleSortChange = (value: string) => {
    setSortBy(value);
    setVisibleCount(ITEMS_PER_PAGE);
    setAnimatedCount(0);
  };

  const handleLoadMore = () => {
    setIsLoadingMore(true);
    const previousCount = visibleCount;
    setTimeout(() => {
      setVisibleCount(prev => prev + ITEMS_PER_PAGE);
      setAnimatedCount(previousCount);
      setIsLoadingMore(false);
    }, 100);
  };

  const handleOpenBottomSheet = () => {
    setTempSortBy(sortBy);
    setTempPriceDuration(priceDuration);
    setIsBottomSheetOpen(true);
  };

  const handleCloseBottomSheet = () => {
    setIsBottomSheetOpen(false);
  };

  const handleValidateFilters = () => {
    setSortBy(tempSortBy);
    setPriceDuration(tempPriceDuration);
    setVisibleCount(ITEMS_PER_PAGE);
    setAnimatedCount(0);
    setIsBottomSheetOpen(false);
  };

  const handleResetFilters = () => {
    setTempSortBy('popular');
    setTempPriceDuration('1-month');
  };

const sortByOptions: RadioOption[] = planType === PLAN_TYPES.INTERNET
  ? [
      { value: 'popular', label: 'Plus populaire' },
      { value: 'cheapest', label: 'Prix le plus bas' },
      { value: 'datas', label: 'Plus de débit' },
      { value: 'tv-channels', label: 'Plus de chaînes TV' },
    ]
  : [
      { value: 'popular', label: 'Plus populaire' },
      { value: 'cheapest', label: 'Prix le plus bas' },
      { value: 'datas', label: 'Plus de données' },
    ];

const priceDurationOptions: RadioOption[] = [
    { value: '1-month', label: '1 mois' },
    { value: '12-months', label: '12 mois' },
    { value: '24-months', label: '24 mois' },
];

  if (loading) {
    return <div className="comparator__loading">Chargement des forfaits...</div>;
  }

  if (error) {
    return <div className="comparator__error">Erreur: {error.message}</div>;
  }

  return (
    <div className={`comparator comparator--${planType}`}>
      {title && <h1 className="comparator__title text-xl font-bold mb-4">{title}</h1>}

      {/* Desktop filters */}
      <div className="comparator__top comparator__top--desktop">
        <strong>{filteredPlans.length} offres{planType !== PLAN_TYPES.INTERNET ? <br /> : ' '}disponibles</strong>
        <div className="comparator__filters">
            <RadioGroup
                name="comparator-sort-by"
                label="Trier par :"
                options={sortByOptions}
                defaultValue="popular"
                onChange={handleSortChange}
            />
            <RadioGroup
                name="comparator-price-duration"
                label="Afficher les<br />prix pour :"
                options={priceDurationOptions}
                defaultValue="1-month"
                onChange={setPriceDuration}
            />
        </div>
        <div className="comparator__info-wrapper" ref={desktopTooltipRef}>
          <button
            type="button"
            className="comparator__info-btn"
            aria-label="Information"
            onClick={() => setIsTooltipOpen(!isTooltipOpen)}
          >
            <img src={iconInformation} alt="" width={36} height={36} />
          </button>
          <div
            className={`comparator__tooltip ${isTooltipOpen ? 'comparator__tooltip--open' : ''}`}
            dangerouslySetInnerHTML={{ __html: infoText }}
          />
        </div>
      </div>

      {/* Tablet filters */}
      <div className="comparator__top comparator__top--tablet">
        <strong>{filteredPlans.length} offres disponibles</strong>
        <div className="comparator__filters comparator__filters--tablet">
          <RadioGroup
            name="comparator-sort-by-tablet"
            label="Trier par :"
            options={sortByOptions}
            defaultValue="popular"
            onChange={handleSortChange}
          />
          <RadioGroup
            name="comparator-price-duration-tablet"
            label="Afficher les prix pour :"
            options={priceDurationOptions}
            defaultValue="1-month"
            onChange={setPriceDuration}
          />
        </div>
        <div className="comparator__info-wrapper" ref={tabletTooltipRef}>
          <button
            type="button"
            className="comparator__info-btn"
            aria-label="Information"
            onClick={() => setIsTooltipOpen(!isTooltipOpen)}
          >
            <img src={iconInformation} alt="" width={36} height={36} />
          </button>
          <div
            className={`comparator__tooltip ${isTooltipOpen ? 'comparator__tooltip--open' : ''}`}
            dangerouslySetInnerHTML={{ __html: infoText }}
          />
        </div>
      </div>

      {/* Mobile filters trigger */}
      <div className="comparator__top comparator__top--mobile">
        <strong>{filteredPlans.length} offres disponibles</strong>
        <div className="comparator__mobile-actions">
          <button
            type="button"
            className="comparator__filter-btn"
            onClick={handleOpenBottomSheet}
          >
            Tri et affichage

            <img src={arrowRight} alt="" aria-hidden="true" width={16} height={16} />
          </button>
          <button
            type="button"
            className="comparator__info-btn"
            aria-label="Information"
            onClick={() => setIsInfoModalOpen(true)}
          >
            <img src={iconInformation} alt="" width={36} height={36} />
          </button>
        </div>
      </div>

      {/* Info Modal for mobile */}
      <Modal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        title="Informations"
      >
        <div
          className="comparator__info-text"
          dangerouslySetInnerHTML={{ __html: infoText }}
        />
      </Modal>

      {/* BottomSheet for mobile filters */}
      <BottomSheet isOpen={isBottomSheetOpen} onClose={handleCloseBottomSheet}>
        <RadioGroup
          name="comparator-sort-by-mobile"
          label="Trier par :"
          options={sortByOptions}
          value={tempSortBy}
          onChange={setTempSortBy}
        />
        <RadioGroup
          name="comparator-price-duration-mobile"
          label="Afficher les prix pour :"
          options={priceDurationOptions}
          value={tempPriceDuration}
          onChange={setTempPriceDuration}
        />
        <div className="comparator__bottom-sheet-actions">
          <button
            type="button"
            className="comparator__reset-btn"
            onClick={handleResetFilters}
          >
            Réinitialiser
          </button>
          <button
            type="button"
            className="comparator__validate-btn"
            onClick={handleValidateFilters}
          >
            Valider
          </button>
        </div>
      </BottomSheet>
      {filteredPlans.length === 0 ? (
        <div className="comparator__empty">
          <img src={lemonSadImg} alt="" width={126} height={203} />
          <p>Désolé, Lemon n&apos;a trouvé aucune offre...</p>
          <button onClick={resetFilters} type="button">
            Réinitialiser les filtres
          </button>
        </div>
      ) : (
        <div className="comparator__grid" id="comparator-grid">
          {plansWithHighlights.map((plan, index) => {
            const isHidden = index >= visibleCount;
            const animationDelay = index >= animatedCount ? (index - animatedCount) * 0.08 : 0;
            return (
              <div
                key={`${sortBy}-${plan.id}`}
                className={isHidden ? 'comparator__card-wrapper comparator__card-wrapper--hidden' : 'comparator__card-wrapper'}
              >
                <ComparatorCard
                  plan={plan}
                  priceDuration={priceDuration}
                  style={{ animationDelay: `${animationDelay}s` }}
                  position={index + 1}
                  planType={planType}
                />
              </div>
            );
          })}
        </div>
      )}
      {hasMoreItems && !isLoadingMore && (
        <button
          type="button"
          className="comparator__load-more"
          onClick={handleLoadMore}
        >
          Charger plus
        </button>
      )}
    </div>
  );
}

export default Comparator;
